import {Inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import * as SockJS from "sockjs-client";
import {InjectableRxStompConfig, RxStompService} from "@stomp/ng2-stompjs";
import {AlertController} from "@ionic/angular";
import {EventsService} from "./events.service";
import {SoftposService} from "./softpos";
import {Plugins} from "@capacitor/core";
import {StompHeaders} from "@stomp/stompjs";
import {Receipt} from "../types/receipt";
import {UtilsService} from "./utils";
import {ConfigurationService} from "./configuration";
import { Logger, LoggingService } from "ionic-logging-service";

// eslint-disable-next-line @typescript-eslint/naming-convention
const {Device} = Plugins;


@Injectable()
export class RestolutionService {

  public currentLogin;
  public credentials;

  //https://ilpastarito.bestorante.com/api/groups/byResto?restoUuid=00000000-f227-82fb-0000-0000f22782fb
  public serverUrl = "https://web.restolution.fi/api/";
  public bestoUrl = "https://bestorante.com";
  public restoUuid = "00000000-f227-82fb-0000-0000f22782fb";
  public crUuid = null;
  public crId = null;

  public hostCr = null; //gets value when cr is selected

  public timeout = 300;
  public msgHeader: StompHeaders = {
    from: "",
    fromModel: "",
    fromManu: "",
    fromOu: "1",
    fromName: "",
    fromType: "GO",
    fromTypeName: "BestoGO",
    fromMachineNumber: "GO1"
  };

  private opts = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    headers: {"Content-Type": "application/json"},
    withCredentials: true
  };

  private logger: Logger;

  constructor(private http: HttpClient, @Inject("remoteStomp") private stomp: RxStompService,
              private alertCtrl: AlertController, public events: EventsService,
              private softpos: SoftposService, private utils: UtilsService, private loggingService: LoggingService) {
    this.logger = loggingService.getLogger("BestoGo.RestolutionService");

    this.timeout = 1000;
    this.findWebSocketMsgHeaders();
  }

  public login(user, psw) {
    const data = {
      username: user,
      password: psw
    };
    this.credentials = data;
    return this.http.post(this.serverUrl + "login.json", data, this.opts);
  }

  public loginComplete(ret: any) {
    this.currentLogin = ret;
    this.logger.debug("loginComplete", "Successful login", ret);
    this.connectWs();
    this.findWebSocketMsgHeaders();
  }

  public doSendSelectCr() {
    if (this.crUuid) {
      const cr = {
        id: this.crId,
        uuid: this.crUuid
      };
      this.hostCr = null;
      this.stomp.publish({destination: "/app/softpos.selectCr", body: JSON.stringify(cr)});
    }
  }

  async deactivateWs() {
    await this.stomp.deactivate().then();
  }

  connectWs() {
    this.stomp.configure(this.getRemoteStompConfig());
    this.stomp.activate();
    this.logger.debug("connectWs", "Activating, ", this.crUuid, this.msgHeader);
    this.stomp.watch("/topic/popup." + this.msgHeader.from + ".control", this.msgHeader).subscribe((msg) => {
      this.logger.trace("connectWs", "Message from control", msg);
    });

    if (this.crUuid) {
      const cr = {
        id: this.crId,
        uuid: this.crUuid
      };
      this.hostCr = null;
      this.stomp.publish({destination: "/app/softpos.selectCr", body: JSON.stringify(cr)});
    }
    this.stomp.watch("/user/queue/response").subscribe((msg) => {
      if (msg.headers && msg.headers.cl) {
        const data = JSON.parse(msg.body);
        this.logger.trace("connectWs", "Found data", data);
        this.events.publishEvent({
          type: msg.headers.cl,
          headers: msg.headers,
          data
        });
        if ("SoftPosSelectCrResponse" === msg.headers.cl) {
          this.hostCr = data;
          this.softpos.hostCrUuid = data.crUuid;
        } else if ("RestoErrorDto" === msg.headers.cl) {
          if (data.message === "No \"user\" header in message") {
            this.logger.error("connectWs", "Not authenticated2", this.credentials, this.timeout);
            setTimeout(() => {
              if (this.credentials) {
                this.logger.debug("connectWs", "Deactivating websocket");
                this.stomp.deactivate().then();
                this.login(this.credentials.username, this.credentials.password).subscribe((ret) => {
                  this.loginComplete(ret);
                  this.timeout = 300;
                });
                this.timeout = this.timeout * 2;
              }
            }, this.timeout);
          }
        } else if (msg.headers.cl === "PopupRegResponse") {
          this.logger.debug("connectWs", "Got registration response", msg);
          if (data && data.machineNumber) {
            this.msgHeader.fromMachineNumber = data.machineNumber;
          }
        }
      }
    });
  }

  public async loginError(error: any) {
    let alert = null;
    let errorMessage = "";

    if (error != null && error.message) {
      errorMessage = error.message;
    }
    if (error.status === 401) {
      alert = await this.alertCtrl.create({
        cssClass: "generic-error",
        header: "Error",
        subHeader: "Incorrect credentials",
        message: "Please check your username and password. <br> (" + errorMessage + ")",
        buttons: ["OK"]
      });
    } else {
      alert = await this.alertCtrl.create({
        cssClass: "generic-error",
        header: "Error",
        subHeader: "Technical problem with login",
        message: "Please check your network connection. <br> (" + errorMessage + ")",
        buttons: ["OK"]
      });
    }

    await alert.present();
    const { role } = await alert.onDidDismiss();
  }

  public sendReceipts(receiptsToSend: Receipt []) {
    if (!receiptsToSend || receiptsToSend.length === 0) {
      this.logger.trace("sendReceipts", "Nothing to send");
      return;
    }
    let dataToSend = receiptsToSend;
    if (dataToSend.length > 10) {
      //we send only 9 receipts
      dataToSend = dataToSend.slice(0, 9);
      //unless all are cancellation receipts, then we send all
      let allCancel = true;
      for (const rr of receiptsToSend) {
        if (!rr.cancelReceipt) {
          allCancel = false;
          break;
        }
      }
      if (allCancel) {
        dataToSend = receiptsToSend;
      }
    }
    for (const rr of receiptsToSend) {
      if (rr.rows) {
        rr.rows = rr.rows.map((r) => this.utils.convertToRow((r as any)));
      }
    }
    this.logger.info("sendReceipts", "Sending sales", dataToSend);
    this.stomp.publish({
      destination: "/app/popup.sendReceipts", body: JSON.stringify({
        receipts: dataToSend, machineNumber:
        this.msgHeader.fromMachineNumber
      }), headers: this.msgHeader
    });
  }

  getRemoteStompConfig() {
    const url = this.serverUrl + "sockjs";
    this.logger.debug("getRemoteStompConfig", "Remote url " + url);
    const remoteStompConfig: InjectableRxStompConfig = {
      // Which server?
      webSocketFactory: () => new SockJS(url),

      // Headers
      // Typical keys: login, passcode, host
      connectHeaders: {
        login: this.credentials ? this.credentials.username : "guest",
        passcode: this.credentials ? this.credentials.password : "guestpsw",
      },

      heartbeatIncoming: 0, // Typical value 0 - disabled
      heartbeatOutgoing: 20000, // Typical value 20000 - every 20 seconds
      reconnectDelay: 5000
    };
    return remoteStompConfig;
  }

  isWsConnected() {
    return this.stomp.connected();
  }

  listRestaurants() {
    return this.http.get(this.serverUrl + "rest/bestogo/" + this.currentLogin.clientId + "/restaurants", this.opts);
  }

  getTableChart() {
    return this.http.get(
      this.serverUrl + "rest/bestogo/" + this.currentLogin.clientId + "/restaurant/" + this.restoUuid + "/tablechart", this.opts
    );
  }

  public findWebSocketMsgHeaders() {
    this.msgHeader.from = "ThisIsBrowser";
    this.msgHeader.fromModel = "Browser";
    this.logger.debug("findWebSocketMsgHeaders", "Checking device", Device);
    if (Device) {
      this.logger.trace("findWebSocketMsgHeaders", "Checking device");
      Device.getId().then((val) => {
        this.msgHeader.from = val.uuid;
        this.logger.debug("findWebSocketMsgHeaders", "Got id", val);
      });
      Device.getInfo().then((val) => {
        this.logger.debug("findWebSocketMsgHeaders", "Got info", val);
        this.msgHeader.fromManu = val.manufacturer;
        this.msgHeader.fromModel = val.model + " " + val.osVersion + " " + val.webViewVersion;
        this.msgHeader.fromName = val.name;
        this.setOu(this.msgHeader.fromOu);
      });
    }
  }

  public setOu(ou) {
    this.stomp.publish({
      destination: "/app/popup.setOu", body: JSON.stringify({
        ouId: ou, deviceId:
        this.msgHeader.from
      }), headers: this.msgHeader
    });
  }
}
